import { useCallback } from 'react';

/**
 * Custom hook for preserving scroll position across data refreshes
 * FAANG-level implementation with graceful degradation and auto-cleanup
 */
export const useScrollPreservation = () => {
  /**
   * Captures current scroll state for later restoration
   * @param {Object} context - Context information for the scroll state
   * @param {string} context.alertId - ID of the alert being acted upon
   * @param {string} context.groupKey - Key of the group containing the alert
   * @param {string} containerSelector - CSS selector for scroll container (optional)
   */
  const captureScrollState = useCallback((context, containerSelector = '[data-scroll-container]') => {
    try {
      const scrollContainer = document.querySelector(containerSelector);
      if (scrollContainer) {
        window.alertScrollState = {
          scrollTop: scrollContainer.scrollTop,
          scrollLeft: scrollContainer.scrollLeft || 0,
          context,
          timestamp: Date.now(),
          containerSelector
        };
      }
    } catch (error) {
      // Silently fail if scroll capture fails
    }
  }, []);

  /**
   * Restores previously captured scroll state
   * @param {number} maxAge - Maximum age in milliseconds for scroll state (default: 5000)
   * @param {Function} onRestore - Callback when scroll is restored
   */
  const restoreScrollState = useCallback((maxAge = 5000, onRestore) => {
    try {
      if (!window.alertScrollState) {
        return false;
      }

      const { scrollTop, scrollLeft, timestamp, containerSelector, context } = window.alertScrollState;
      const age = Date.now() - timestamp;

      // Check if scroll state is still valid (not too old)
      if (age > maxAge) {
        window.alertScrollState = null;
        return false;
      }

      // Use requestAnimationFrame for smooth restoration
      requestAnimationFrame(() => {
        const scrollContainer = document.querySelector(containerSelector);
        if (scrollContainer) {
          scrollContainer.scrollTop = scrollTop;
          if (scrollLeft > 0) {
            scrollContainer.scrollLeft = scrollLeft;
          }
          

          // Call onRestore callback if provided
          onRestore?.(context);
          
          // Clean up after successful restore
          window.alertScrollState = null;
        } else {
          // Scroll container not found for restoration
        }
      });

      return true;
    } catch (error) {
      // Failed to restore scroll state
      return false;
    }
  }, []);

  /**
   * Clears any existing scroll state (useful for cleanup)
   */
  const clearScrollState = useCallback(() => {
    if (window.alertScrollState) {
      window.alertScrollState = null;
    }
  }, []);

  /**
   * Checks if scroll state exists and is valid
   * @param {number} maxAge - Maximum age in milliseconds (default: 5000)
   */
  const hasValidScrollState = useCallback((maxAge = 5000) => {
    if (!window.alertScrollState) {
      return false;
    }
    const age = Date.now() - window.alertScrollState.timestamp;
    return age <= maxAge;
  }, []);

  return {
    captureScrollState,
    restoreScrollState,
    clearScrollState,
    hasValidScrollState
  };
};

export default useScrollPreservation;